import { ActionTree } from 'vuex';
import {
  mutationSetLoading,
  mutationSetTemplates,
  mutationSetPagination,
  mutationSetFolders,
  mutationsSetBreadcrumbs,
  mutationsSetFolderTree,
  mutationsSetFolderId,
  mutationSetLoaded,
} from '@/dealroom/template-library/store/template-library.mutations';
import { IDocument } from '@/types/Document';
import { TemplateService } from '@/api/templates/template.service';
import { RootState } from '@/store/store';
import { ITemplateLibraryState } from '@/dealroom/template-library/store/template-library.store';
import { FolderService } from '@/api/folders/services/folder.service';

export const actionLoadTemplates = 'loadTemplates';
export const actionLoadTemplatesSuccess = 'loadTemplatesSuccess';
export const actionLoadTemplatesFailed = 'loadTemplatesFailed';

export const actionLoadFolders = 'loadFolders';
export const actionLoadFoldersSuccess = 'loadFoldersSuccess';
export const actionLoadFoldersFailed = 'loadFoldersFailed';

export const actionLoadBreadcrumbs = 'loadBreadcrumbs';
export const actionLoadBreadcrumbsSuccess = 'loadBreadcrumbsSuccess';
export const actionLoadBreadcrumbsFailed = 'loadBreadcrumbsFailed';

export const actionLoadFolderTree = 'loadFolderTree';
export const actionLoadFolderTreeFailed = 'loadFolderTreeFailed';
export const actionLoadFolderTreeSuccess = 'loadFolderTreeSuccess';

export const actionSetFolderId = 'setFolderId';

import { translate } from '@/helpers/translation.helper';
import { EFolderType } from '@/folders/types/enums/folder-type';
import { IFolder } from '@/folders/types/folder';
import { IFolderTree } from '@/folders/types/folder-tree';

export const actions: ActionTree<ITemplateLibraryState, RootState> = {
  async [actionLoadTemplates](
    { dispatch, commit, state },
    { pageIndex, search, folderId }: { pageIndex: number; search?: string; folderId?: string }
  ) {
    commit(mutationSetLoading, true);
    const index: number = pageIndex || 1;
    const { limit }: { limit: number } = state.pagination;
    const offset: number = limit * (index - 1);
    commit(mutationSetPagination, { limit, offset, currentPageIndex: pageIndex, search });
    try {
      const response: {
        templates: IDocument[];
        totalCount: number;
      } = await TemplateService.loadTemplates(folderId, offset, limit - 1, search);
      dispatch(actionLoadTemplatesSuccess, response);
    } catch (error) {
      dispatch(actionLoadTemplatesFailed, error);
    }
  },
  [actionLoadTemplatesSuccess](
    { commit },
    { templates, totalCount }: { templates: IDocument[]; totalCount: number }
  ) {
    commit(mutationSetTemplates, { templates, totalCount });
    commit(mutationSetLoaded, true);
    commit(mutationSetLoading, false);
  },
  [actionLoadTemplatesFailed]({ commit, dispatch }) {
    commit(mutationSetTemplates, { templates: [], totalCount: 0 });
    commit(mutationSetLoaded, true);
    commit(mutationSetLoading, false);
    dispatch('alert/showDangerAlert', translate('failed-to-load-templates'), {
      root: true,
    });
  },
  async [actionLoadFolders]({ dispatch }, id?: string) {
    try {
      const folders: IFolder[] = await FolderService.loadFolders(id, EFolderType.Template);
      dispatch(actionLoadFoldersSuccess, folders);
    } catch (err) {
      dispatch(actionLoadFoldersFailed);
    }
  },
  [actionLoadFoldersSuccess]({ commit }, folders: IFolder[]) {
    commit(mutationSetFolders, folders);
  },
  [actionLoadFoldersFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-load-templates'), {
      root: true,
    });
  },
  async [actionLoadBreadcrumbs]({ dispatch }, id?: string) {
    try {
      const breadcrumbs: IFolder[] = await FolderService.loadBreadcrumbs(id, EFolderType.Template);
      dispatch(actionLoadBreadcrumbsSuccess, breadcrumbs);
    } catch (err) {
      dispatch(actionLoadBreadcrumbsFailed);
    }
  },
  [actionLoadBreadcrumbsSuccess]({ commit }, breadcrumbs: IFolder[]) {
    const rootFolder: IFolder = { id: null, name: 'Start' };
    commit(mutationsSetBreadcrumbs, [rootFolder, ...breadcrumbs]);
  },
  [actionLoadBreadcrumbsFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-load-templates'), {
      root: true,
    });
  },
  async [actionLoadFolderTree]({ dispatch }) {
    try {
      const folderTree: IFolderTree = await FolderService.loadFolderTree(EFolderType.Template);
      dispatch(actionLoadFolderTreeSuccess, folderTree);
    } catch (err) {
      dispatch(actionLoadFolderTreeFailed);
    }
  },
  [actionLoadFolderTreeSuccess]({ commit }, folderTree: IFolderTree) {
    commit(mutationsSetFolderTree, folderTree);
  },
  [actionLoadFolderTreeFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-load-templates'), {
      root: true,
    });
  },
  [actionSetFolderId]({ commit }, folderId: string) {
    commit(mutationsSetFolderId, folderId);
  },
};
